import SelectableItem, {
  type SelectableItemType,
  type SelectableItemController,
} from './SelectableItem';
import { raw } from '@nx-js/observer-util';
import type DropHandler from './DropHandler';

/**
 * Base class for handling common behavior for draggable
 * things. Inherit from this for your own draggable item
 * types. DraggableItems exist to hand off records between different
 * drop zones / handlers.
 */
class DraggableItem<
  ItemType extends SelectableItemType = SelectableItemType,
  Controller extends SelectableItemController = SelectableItemController,
  DropType extends SelectableItemType = ItemType
> extends SelectableItem<ItemType, Controller> {
  listKey: string;
  listIndex: number;
  listIndexWithPreviews: number;

  /**
   * Returns true if this item is draggable, false otherwise.
   */
  canDrag(): boolean {
    return true;
  }

  /**
   * Returns true if this item can be dragged along with another draggable item. Currently
   * only items of the same type can be dragged together.
   */
  canDragWithItem(otherItem: DraggableItem): boolean {
    return this.type === otherItem.type;
  }

  /**
   * Called when this DraggableItem starts to be dragged. Useful for
   * setting more expensive state that you don't want to calculate
   * until you know a drag is happening.
   */
  onBegin() {}

  /**
   * Called when this DraggableItem is hovering over an acceptable DropHandler.
   */
  onHover({ dropHandler }: { dropHandler: DropHandler }) {
    dropHandler.onHover({ draggedItem: this });
  }

  /**
   * Called when a drag-and-drop is cancelled.
   */
  onCancel() {
    this.onFinish();
  }

  /**
   * Prepares the item after the drop has completed. This is useful if you need to create a new
   * item or transform something.
   */
  prepareDroppedItem(dropHandler: DropHandler): DropType {
    return this.item as unknown as DropType;
  }

  /**
   * Persists the dropped item, if needed. In cases where a new record needs to be created as part of the drop,
   * this is where that new record should be persisted.
   *
   * It is passed the value returned by prepareDroppedItem().
   */
  async persistDroppedItem(droppedItem: DropType): Promise<DropType> {
    return this.item as unknown as DropType;
  }

  /**
   * Called when a drag-and-drop finishes. Sometimes useful to clean up
   * code within the original controller.
   */
  onEnd({ dropHandler }) {
    if (raw(dropHandler.controller) !== raw(this.controller)) {
      this.removeFromController(dropHandler);
    }

    this.onFinish();
  }

  /**
   * Shared cleanup method that is called both by onCancel and onEnd.
   */
  onFinish() {}

  /**
   * Called when the item is dropped in a controller other than its original controller.
   * Used to clean up/remove the item from the controller.
   */
  removeFromController(dropHandler: DropHandler) {}

  /**
   * Called when the item has been persisted by it's new drop target.
   * Useful for cleaning up state within the original controller.
   */
  onDropPersisted(dropHandler: DropHandler) {}
}

export default DraggableItem;
